import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from 'components/ui/button';
import Select from 'components/generic/Select';

const platformOptions = [
    { value: 1, label: 'IVS' },
    { value: 2, label: 'YouTube' },
    { value: 3, label: 'Facebook Live' },
    { value: 4, label: 'Twitch' },
    { value: 5, label: 'LinkedIn' },
    { value: 6, label: 'Twitter' },
];

/**
 * PlatformConnectionModal Component
 * Modal for adding or editing platform RTMP destinations
 */
const PlatformConnectionModal = ({
    isOpen,
    onClose,
    onSave,
    destination = null,
    loading = false
}) => {
    const [platform, setPlatform] = useState('');
    const [rtmpUrl, setRtmpUrl] = useState('');
    const [errors, setErrors] = useState({});

    // Initialize form if editing
    useEffect(() => {
        if (destination) {
            setPlatform(destination.platform || '');
            setRtmpUrl(destination.rtmp_url || '');
        } else {
            setPlatform('');
            setRtmpUrl('');
        }
        setErrors({});
    }, [destination, isOpen]);

    const validateForm = () => {
        const newErrors = {};

        if (!platform) {
            newErrors.platform = 'Please select a platform';
        }

        if (!rtmpUrl) {
            newErrors.rtmpUrl = 'RTMP URL is required';
        } else if (!rtmpUrl.startsWith('rtmp://') && !rtmpUrl.startsWith('rtmps://')) {
            newErrors.rtmpUrl = 'RTMP URL must start with rtmp:// or rtmps://';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        onSave({
            platform,
            rtmpUrl,
            destinationId: destination?.stream_destination_id || null,
        });
    };

    const handleClose = () => {
        setPlatform('');
        setRtmpUrl('');
        setErrors({});
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="relative bg-card rounded-[14px] p-6 w-full max-w-md mx-4 shadow-xl">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold">
                        {destination ? 'Edit Platform Connection' : 'Add Platform Connection'}
                    </h2>
                    <button
                        onClick={handleClose}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    {/* Platform Selection */}
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium">Platform</label>
                        <Select
                            options={platformOptions}
                            value={platform}
                            onChange={setPlatform}
                            placeholder="Select platform"
                            disabled={!!destination} // Disable platform change when editing
                        />
                        {errors.platform && (
                            <p className="text-xs text-red-500">{errors.platform}</p>
                        )}
                    </div>

                    {/* RTMP URL Input */}
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium">RTMP URL</label>
                        <input
                            type="text"
                            value={rtmpUrl}
                            onChange={(e) => setRtmpUrl(e.target.value)}
                            placeholder="rtmps://a.rtmp.youtube.com/live2/xxxx-xxxx-xxxx-xxxx"
                            className="px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-button-coloured"
                        />
                        {errors.rtmpUrl && (
                            <p className="text-xs text-red-500">{errors.rtmpUrl}</p>
                        )}
                        <p className="text-xs text-muted-foreground">
                            Include the stream key in the URL
                        </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 mt-4">
                        <Button
                            type="button"
                            onClick={handleClose}
                            className="flex-1 bg-accent text-accent-foreground hover:bg-accent/80"
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="flex-1 bg-button-coloured hover:bg-button-coloured/90"
                            disabled={loading}
                        >
                            {loading ? 'Saving...' : destination ? 'Update' : 'Add'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PlatformConnectionModal;
